// frontend/src/pages/ReportsPage.tsx
//
// Orquestador del módulo de reports. Es dueña de filtros, nivel, orden,
// paginación y columnas visibles, y pasa a la tabla los bloques filtrados
// COMPLETOS (para totales y export) junto a la página visible — el mismo reparto
// que PedidosVentasPage, para que los totales nunca se calculen sobre la página.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'
import { EmptyState } from '@/components/common/EmptyState'
import { PageHeader } from '@/components/common/PageHeader'
import { Pagination } from '@/components/common/Pagination'
import { TableToolbar } from '@/components/common/TableToolbar'
import { ColumnsMenu } from '@/components/common/ColumnsMenu'
import { ReportFilters } from '@/components/reports/ReportFilters'
import { ReportTable } from '@/components/reports/ReportTable'
import { ReportsTabs } from '@/components/reports/ReportsTabs'
import { ReportSkeleton } from '@/components/reports/ReportSkeleton'
import { colLabel, REPORT_DEFS, reportByRoute, type ReportDef } from '@/components/reports/reportDefs'
import { buildDetailMatcher, buildFilterPredicates, catalogosMultiText } from '@/components/reports/filterPredicates'
import { campo } from '@/components/reports/formato'
import { clavesBloqueDuplicadas } from '@/components/reports/marcasReport'
import { useReport } from '@/hooks/useReports'
import { useReportFilters } from '@/hooks/useReportFilters'
import { useVisibleReportCols } from '@/hooks/useVisibleReportCols'
import { exportReportExcel } from '@/utils/exportReportExcel'
import {
  bloquesConCoincidencia, filterBlocks, groupBlocks, rowsOfType, type ReportBlock,
} from '@/utils/reportBlocks'
import type { AnyReportRow } from '@/types'

/** Se pagina por bloques: cortar por filas separaría una cabecera de su detalle. */
const BLOQUES_POR_PAGINA = 50

export default function ReportsPage() {
  const { report } = useParams<{ report: string }>()
  const def = reportByRoute(report ?? '') ?? REPORT_DEFS.rawMaterials
  // La key remonta el subárbol al cambiar de pestaña, para que el estado interno
  // de la tabla (filas expandidas) no se arrastre de un report a otro.
  return <ReportView key={def.key} def={def} />
}

function ReportView({ def }: { def: ReportDef }) {
  const { t } = useTranslation()
  const dark = useAppStore(s => s.dark)
  const { rows, isLoading, isError, updatedAt } = useReport(def.key)
  const {
    filters, setFilter, clearFilters, activeCount, activeLevel, aggLevels, setLevels,
    soloCoincidencias, setSoloCoincidencias,
  } =
    useReportFilters(def)
  const { visible, cols, excelCols, menuItems, toggle } = useVisibleReportCols(def)

  /**
   * Bloques que el usuario ha abierto o cerrado A MANO, por encima de los que
   * abre la búsqueda. `true` abierto, `false` cerrado.
   *
   * Hacen falta las dos cosas, y no un simple conjunto de abiertos: si el
   * despliegue automático marca un bloque y el usuario lo cierra, un conjunto de
   * abiertos no sabría distinguir «nunca lo tocó» de «lo cerró él», y el bloque
   * se volvería a abrir en el siguiente repintado.
   */
  const [manual, setManual] = useState<Map<number, boolean>>(new Map())
  const [sortKey, setSortKey] = useState('ordenBloque')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [exporting, setExporting] = useState(false)

  const allBlocks = useMemo(() => groupBlocks(rows as AnyReportRow[]), [rows])

  /**
   * Centinela de identidad: dos bloques con la misma clave de negocio comparten
   * marca de fila, así que marcar uno enciende el otro.
   *
   * No rompe nada y por eso hay que avisarlo — misma familia que el centinela de
   * `tipoFila` en `useReports`. Si salta, a `blockKeyFields` del informe le falta
   * un campo.
   */
  useEffect(() => {
    const repetidas = clavesBloqueDuplicadas(def, allBlocks)
    if (repetidas.length > 0) {
      console.warn(
        `[reports/${def.key}] ${repetidas.length} clave(s) de bloque repetidas: `
        + 'los bloques que las comparten comparten marca de fila. '
        + `Campos declarados: ${def.blockKeyFields.join(', ')}.`,
      )
    }
  }, [def, allBlocks])

  /**
   * Qué términos de búsqueda son un valor COMPLETO y no un fragmento.
   *
   * Lo que decide que filtrar por `ARF32120414` NO traiga también
   * `XARF32120414`, que es otro artículo cuyo código contiene al primero. Se
   * calcula sobre TODAS las filas y no sobre las filtradas: el catálogo describe
   * lo que existe, no lo que queda después de filtrar.
   */
  const catalogos = useMemo(() => catalogosMultiText(def, rows as AnyReportRow[]), [def, rows])

  const filtered = useMemo(() => {
    const { header, detail } = buildFilterPredicates<AnyReportRow>(def, filters, catalogos)
    return filterBlocks(allBlocks, header, detail)
  }, [allBlocks, def, filters, catalogos])

  /**
   * Ordena BLOQUES, no filas: las sublíneas viajan con su cabecera. Sin esto,
   * ordenar por una columna desmontaría la jerarquía.
   */
  const sorted = useMemo(() => {
    if (sortKey === 'ordenBloque' && sortDir === 'asc') return filtered
    const dir = sortDir === 'asc' ? 1 : -1
    return [...filtered].sort((a, b) => {
      const va = campo(a.header, sortKey)
      const vb = campo(b.header, sortKey)
      // Los vacíos van siempre al final, en los dos sentidos: son «no aplica», y
      // arrastrarlos arriba al invertir el orden solo estorba.
      const ea = va == null || va === ''
      const eb = vb == null || vb === ''
      if (ea && eb) return a.key - b.key
      if (ea) return 1
      if (eb) return -1
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
      return String(va).localeCompare(String(vb), 'es', { numeric: true }) * dir
    })
  }, [filtered, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(sorted.length / BLOQUES_POR_PAGINA))
  const pageSafe = Math.min(page, totalPages)
  const pageBlocks: ReportBlock<AnyReportRow>[] = useMemo(
    () => sorted.slice((pageSafe - 1) * BLOQUES_POR_PAGINA, pageSafe * BLOQUES_POR_PAGINA),
    [sorted, pageSafe],
  )

  /**
   * La sublínea que ha traído aquí a su bloque. `null` mientras no haya filtros de
   * detalle, que es cuando no hay nada que explicar.
   */
  const coincide = useMemo(
    () => buildDetailMatcher<AnyReportRow>(def, filters, catalogos),
    [def, filters, catalogos],
  )

  /** Bloques que abre sola la búsqueda, por tener una sublínea que casa. */
  const auto = useMemo(
    () => bloquesConCoincidencia(pageBlocks, coincide),
    [pageBlocks, coincide],
  )

  /**
   * Al cambiar la búsqueda se olvidan los ajustes manuales.
   *
   * Sin esto, un bloque cerrado a mano seguiría cerrado en la búsqueda siguiente
   * —otra búsqueda, otras coincidencias— y parecería que el despliegue automático
   * no funciona en unos bloques sí y en otros no.
   */
  const firmaFiltros = JSON.stringify(filters)
  useEffect(() => { setManual(new Map()) }, [firmaFiltros])

  /** Los que están abiertos: lo que diga el usuario y, si no dijo nada, la búsqueda. */
  const expanded = useMemo(() => {
    const out = new Set<number>()
    for (const b of pageBlocks) {
      if (manual.get(b.key) ?? auto.has(b.key)) out.add(b.key)
    }
    return out
  }, [pageBlocks, manual, auto])

  const toggleBlock = useCallback((key: number) => {
    setManual(prev => {
      const next = new Map(prev)
      next.set(key, !(prev.get(key) ?? auto.has(key)))
      return next
    })
  }, [auto])

  const allExpanded = pageBlocks.length > 0 && pageBlocks.every(b => expanded.has(b.key))

  function toggleAll() {
    // Se escribe como decisión manual de cada bloque de la página: así «plegar
    // todo» también cierra los que había abierto la búsqueda.
    setManual(prev => {
      const next = new Map(prev)
      for (const b of pageBlocks) next.set(b.key, !allExpanded)
      return next
    })
  }

  function onSort(key: string) {
    if (key === sortKey) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(key); setSortDir('asc') }
    setPage(1)
  }

  async function onExport() {
    setExporting(true)
    try {
      // Se exporta el conjunto filtrado completo, no la página: el usuario espera
      // el informe que ha construido con los filtros, no lo que cabe en pantalla.
      await exportReportExcel(def, sorted, excelCols, t, new Date())
    } finally {
      setExporting(false)
    }
  }

  /**
   * Recuento con el nombre del dominio de cada pantalla, y UNO solo.
   *
   * Antes el pie decía «90 rows» y la paginación «90 blocks» dos centímetros más
   * abajo: lo mismo contado con dos nombres.
   */
  const recuento = t(def.unitKey, {
    count: sorted.length,
    formattedCount: sorted.length.toLocaleString('es-ES'),
  })

  /** Marca de tiempo del dato que se está viendo. */
  const sello = updatedAt
    ? t('reports.generatedAt', {
        stamp: new Date(updatedAt).toLocaleString('es-ES', {
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit',
        }),
      })
    : ''

  /**
   * Cuántos artículos distintos hay tras el filtro, cuando el report deduplica.
   * Se calcula aquí y no en la tabla porque el aviso vive en la barra, junto al
   * recuento, donde se lee antes de mirar los totales.
   */
  const avisoDedup = useMemo(() => {
    const key = def.dedupeBy
    // Con varios niveles a la vez basta con que UNO deduplique para que el aviso
    // haga falta: el de cabecera nunca deduplica, los de detalle sí.
    const conDedup = key ? aggLevels.filter(n => n !== def.headerType) : []
    if (!key || conDedup.length === 0) return ''
    const distintos = new Set(
      conDedup.flatMap(n => rowsOfType(sorted, n))
        .map(r => campo(r, key))
        .filter(v => v != null && v !== ''),
    ).size
    return t('reports.dedupNote', {
      count: distintos,
      formattedCount: distintos.toLocaleString('es-ES'),
    })
  }, [def, sorted, aggLevels, t])

  const subtitle = isLoading
    // En blanco mientras carga: antes saltaba de «0 blocks» al total real.
    ? (activeCount > 0 ? t('common.activeFilter', { count: activeCount }) : '')
    : [recuento, activeCount > 0 ? t('common.activeFilter', { count: activeCount }) : null]
        .filter(Boolean).join(' · ')

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        dark={dark}
        title={t(def.titleKey)}
        subtitle={subtitle}
        actions={<ReportsTabs />}
      />

      <ReportFilters
        def={def}
        filters={filters}
        onChange={(k, v) => { setFilter(k, v); setPage(1) }}
        onClear={() => { clearFilters(); setPage(1) }}
        resumen={isLoading ? undefined : recuento}
        rows={rows as AnyReportRow[]}
        activeLevel={activeLevel}
        aggLevels={aggLevels}
        onLevels={ls => { setLevels(ls); setPage(1) }}
        soloCoincidencias={soloCoincidencias}
        onSoloCoincidencias={setSoloCoincidencias}
      />

      <div className={`rounded-2xl border ${dark ? 'border-white/10 bg-slate-900/40' : 'border-slate-200 bg-white shadow-sm'}`}>
        <TableToolbar
          dark={dark}
          left={
            <span className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] ${
              dark ? 'text-slate-400' : 'text-slate-500'
            }`}>
              <span>{isLoading ? '' : recuento}</span>
              {/* El dato cambia todos los días según avanza la fábrica: sin la
                  marca de tiempo no se sabe de cuándo es lo que se está viendo. */}
              {sello && <span className="tabular-nums">{sello}</span>}
              {/* El aviso de deduplicación tiene que estar A LA VISTA: un
                  componente compartido aparece bajo cada terminado que lo usa —hay
                  uno en 47— y quien sume la columna a mano sacaría un número doce
                  veces mayor que el real. */}
              {avisoDedup && (
                <span
                  className={dark ? 'text-amber-300/80' : 'text-amber-700'}
                  title={t('reports.tooltips.dedup')}
                >
                  {avisoDedup}
                </span>
              )}
            </span>
          }
          right={
            <div className="flex items-center gap-2">
              <button
                onClick={toggleAll}
                disabled={sorted.length === 0}
                className={`text-[11px] px-2.5 py-1.5 rounded-lg border font-medium transition-colors ${
                  dark
                    ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 cursor-pointer'
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm cursor-pointer'
                }`}
              >
                {allExpanded ? t('reports.collapseAll') : t('reports.expandAll')}
              </button>
              <ColumnsMenu
                dark={dark}
                items={menuItems.map(k => ({
                  key: k,
                  // El mismo rótulo que la cabecera de la tabla, y por tanto en el
                  // idioma de la UI. El literal inglés del cliente se reserva para
                  // el fichero: ahí es un formato acordado, no texto de interfaz.
                  label: colLabel(def.cols.find(c => c.key === k)!, t),
                }))}
                visible={visible}
                onToggle={toggle}
                btnLabel={t('reports.columns')}
                title={t('reports.columnsTitle')}
              />
              <button
                onClick={onExport}
                disabled={exporting || sorted.length === 0}
                className={`text-[11px] px-2.5 py-1.5 rounded-lg border font-medium transition-colors ${
                  exporting || sorted.length === 0
                    ? (dark ? 'border-white/5 text-slate-600 cursor-not-allowed' : 'border-slate-100 text-slate-300 cursor-not-allowed')
                    : (dark
                        ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10 cursor-pointer'
                        : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50 shadow-sm cursor-pointer')
                }`}
              >
                {exporting ? t('reports.exporting') : t('reports.exportExcel')}
              </button>
            </div>
          }
        />

        {isLoading ? (
          <ReportSkeleton dark={dark} cols={cols} />
        ) : isError ? (
          <EmptyState dark={dark} title={t('reports.errorTitle')} description={t('reports.errorDesc')} />
        ) : sorted.length === 0 ? (
          <EmptyState
            dark={dark}
            title={t('reports.emptyTitle')}
            description={activeCount > 0 ? t('reports.emptyFiltered') : t('reports.emptyDesc')}
          />
        ) : (
          <>
            <ReportTable
              def={def}
              cols={cols}
              blocks={sorted}
              pageBlocks={pageBlocks}
              aggLevels={aggLevels}
              soloCoincidencias={soloCoincidencias}
              expanded={expanded}
              onToggle={toggleBlock}
              coincide={coincide}
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
            />
            {totalPages > 1 && (
              <Pagination
                dark={dark}
                page={pageSafe}
                totalPages={totalPages}
                totalItems={sorted.length}
                itemLabel={t(`${def.unitKey}_plural`, { defaultValue: '' })}
                pageLabel={t('common.page')}
                ofLabel={t('common.of')}
                onFirst={() => setPage(1)}
                onPrev={() => setPage(p => Math.max(1, p - 1))}
                onNext={() => setPage(p => Math.min(totalPages, p + 1))}
                onLast={() => setPage(totalPages)}
              />
            )}
          </>
        )}
      </div>
    </div>
  )
}
